import api from '../api';
import type {
  SmartStartPipelineSummary,
  ClassificationResult,
  SmartStartSessionRow,
  HydratedSmartStartSession,
  OrchestratorProposal,
  HydratedOrchestratorSession,
  DeepLinkIssueResponse,
  PlanStep,
} from '../types/smartStart';

export const listPipelines = async (): Promise<SmartStartPipelineSummary[]> => {
  const { data } = await api.get('/api/smart-start/pipelines');
  return data.pipelines || [];
};

export interface AttachedDocument {
  name: string;
  text: string;
  size?: number;
  mime?: string;
}

export const createSmartStartSession = async (
  userGoal: string,
  projectId?: number | null,
  attachedDocuments?: AttachedDocument[]
): Promise<SmartStartSessionRow> => {
  const { data } = await api.post('/api/smart-start/sessions', {
    user_goal: userGoal,
    project_id: projectId ?? null,
    attached_documents: attachedDocuments || [],
  });
  return data.session;
};

/**
 * Upload a file for use in a Smart Start session. Uses the dedicated
 * /api/smart-start/upload-document endpoint which holds the file in memory
 * (never writes to backend/uploads/), extracts text, and returns it.
 * The extracted text is what gets fed to intake, AI Council, and SDA
 * adapters — the source file is ephemeral.
 */
export const uploadSmartStartDocument = async (
  file: File
): Promise<AttachedDocument> => {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await api.post('/api/smart-start/upload-document', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  if (!data?.success) {
    throw new Error(data?.error || 'Upload failed');
  }
  return {
    name: data.filename || file.name,
    text: data.text || '',
    size: typeof data.size === 'number' ? data.size : file.size,
    mime: data.mime || file.type,
  };
};

export interface LatestSmartStartDocumentsResponse {
  session_id: number | null;
  session_title: string | null;
  session_updated_at: string | null;
  documents: AttachedDocument[];
}

/**
 * Fetch the most recent SmartStart session's attached documents for the user,
 * scoped to a project. Used to carry SmartStart-uploaded documents into other
 * sections (e.g. AI Council) so users don't have to re-upload.
 *
 * Pass null/undefined projectId to look up sessions with no project. The
 * server only matches the (user_id, project_id) pair exactly — never returns
 * docs from a different project.
 */
export const getLatestSmartStartDocuments = async (
  projectId: number | null | undefined
): Promise<LatestSmartStartDocumentsResponse> => {
  const params: Record<string, string> = {};
  if (projectId != null) params.project_id = String(projectId);
  const { data } = await api.get('/api/smart-start/latest-attached-documents', { params });
  return {
    session_id: data?.session_id ?? null,
    session_title: data?.session_title ?? null,
    session_updated_at: data?.session_updated_at ?? null,
    documents: Array.isArray(data?.documents) ? data.documents : [],
  };
};

export const fetchProjectIngestedDocuments = async (
  projectId: number
): Promise<AttachedDocument[]> => {
  const { data } = await api.get(`/api/smart-start/project/${projectId}/ingested-documents`);
  return Array.isArray(data?.documents) ? data.documents : [];
};

export interface SmartStartSessionDocumentsResponse {
  session_id: number;
  session_title: string | null;
  session_updated_at: string | null;
  session_project_id: number | null;
  documents: AttachedDocument[];
}


export const getSmartStartSessionDocuments = async (
  sessionId: number
): Promise<SmartStartSessionDocumentsResponse> => {
  const { data } = await api.get(`/api/smart-start/sessions/${sessionId}/attached-documents`);
  return {
    session_id: typeof data?.session_id === 'number' ? data.session_id : sessionId,
    session_title: data?.session_title ?? null,
    session_updated_at: data?.session_updated_at ?? null,
    session_project_id: data?.session_project_id ?? null,
    documents: Array.isArray(data?.documents) ? data.documents : [],
  };
};

export interface PickerPipeline {
  id: number;
  pipeline_key: string;
  name: string;
  description: string | null;
  short_desc: string;
  subcomponent_label: string;
  subcomponent_order: number;
  display_order: number;
  routing_keywords: string[];
  use_cases: string[];
  tags: string[];
}

export interface PickerSubcomponent {
  label: string;
  order: number;
  pipelines: PickerPipeline[];
}

export interface PickerCategory {
  slug: string;
  display_name: string;
  short_desc: string;
  icon_name: string;
  color_ramp: string;
  display_order: number;
  subcomponents: PickerSubcomponent[];
}

export interface PickerTag {
  slug: string;
  display_name: string;
  display_order: number;
}

export interface PickerPinned extends PickerPipeline {
  category_slug: string;
}

export interface PipelinePickerPayload {
  categories: PickerCategory[];
  tags: PickerTag[];
  pinned: PickerPinned[];
  pipeline_count: number;
  max_pins: number;
}

export const fetchPipelinePicker = async (): Promise<PipelinePickerPayload> => {
  const { data } = await api.get('/api/smart-start/picker');
  return {
    categories: Array.isArray(data?.categories) ? data.categories : [],
    tags: Array.isArray(data?.tags) ? data.tags : [],
    pinned: Array.isArray(data?.pinned) ? data.pinned : [],
    pipeline_count: Number(data?.pipeline_count ?? 0),
    max_pins: Number(data?.max_pins ?? 6),
  };
};

export class PipelinePinLimitError extends Error {
  limit: number;
  constructor(limit: number) {
    super('pin_limit_reached');
    this.name = 'PipelinePinLimitError';
    this.limit = limit;
  }
}

export const pinPipeline = async (pipelineKey: string): Promise<void> => {
  try {
    await api.post(`/api/smart-start/pipelines/${encodeURIComponent(pipelineKey)}/pin`);
  } catch (err: any) {
    if (err?.response?.status === 409 && err.response.data?.error === 'pin_limit_reached') {
      throw new PipelinePinLimitError(Number(err.response.data?.limit ?? 6));
    }
    throw err;
  }
};

export const unpinPipeline = async (pipelineKey: string): Promise<void> => {
  await api.delete(`/api/smart-start/pipelines/${encodeURIComponent(pipelineKey)}/pin`);
};

export const classifySession = async (
  sessionId: number
): Promise<{
  session: SmartStartSessionRow;
  classification: ClassificationResult;
  pipelines: SmartStartPipelineSummary[];
}> => {
  const { data } = await api.post(`/api/smart-start/sessions/${sessionId}/classify`, undefined, { timeout: 0 });
  return {
    session: data.session,
    classification: data.classification,
    pipelines: data.pipelines || [],
  };
};

// ─── Smart Router assistant (answer-vs-route) ────────────────────────────────

export interface SmartRouterCitation {
  document_id: number;
  name?: string;
  relevance?: number;
}

export interface SmartRouterModuleSuggestion {
  module: string;
  name: string;
  target_screen: string;
  target_route: string;
  reason?: string;
  confidence?: number;
}

export interface SmartRouterAnswer {
  mode: 'answer' | 'route' | 'answer_and_route';
  answer: string;
  route: SmartRouterModuleSuggestion | null;
  auto_route: boolean;
  suggested_modules: SmartRouterModuleSuggestion[];
  clarifying_question: string | null;
  used_document: boolean;
  citations?: SmartRouterCitation[];
}

export interface SmartRouterTurn {
  role: 'user' | 'assistant';
  content: string;
}

export const askSmartRouter = async (
  message: string,
  conversation: SmartRouterTurn[] = [],
  projectId?: number | null
): Promise<SmartRouterAnswer> => {
  const { data } = await api.post(
    '/api/smart-router/ask',
    { message, conversation, project_id: projectId ?? null },
    { timeout: 0 }
  );
  return data.data as SmartRouterAnswer;
};

export const selectPipeline = async (
  sessionId: number,
  pipelineKey: string
): Promise<HydratedSmartStartSession> => {
  const { data } = await api.post(
    `/api/smart-start/sessions/${sessionId}/select-pipeline`,
    { pipeline_key: pipelineKey }
  );
  return { session: data.session, stageRuns: data.stageRuns };
};

export const listSessions = async (): Promise<SmartStartSessionRow[]> => {
  const { data } = await api.get('/api/smart-start/sessions');
  return data.sessions || [];
};

export const getSession = async (
  sessionId: number
): Promise<HydratedSmartStartSession> => {
  const { data } = await api.get(`/api/smart-start/sessions/${sessionId}`);
  return { session: data.session, stageRuns: data.stageRuns };
};

export const deleteSession = async (sessionId: number): Promise<void> => {
  await api.delete(`/api/smart-start/sessions/${sessionId}`);
};

export interface SmartStartAuditEvent {
  ts: string;
  kind: string;
  stage_index?: number;
  stage_key?: string;
  detail: string;
}

export const getSessionAudit = async (sessionId: number): Promise<SmartStartAuditEvent[]> => {
  const { data } = await api.get(`/api/smart-start/sessions/${sessionId}/audit`);
  return data.events || [];
};

export const downloadStageFile = async (
  sessionId: number,
  stageIndex: number,
  fileName: string
): Promise<void> => {
  const response = await api.get(
    `/api/smart-start/sessions/${sessionId}/stages/${stageIndex}/files/${encodeURIComponent(fileName)}`,
    { responseType: 'blob' }
  );
  const blob = new Blob([response.data as BlobPart]);
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};

export interface AdminPipeline {
  id: number;
  tenant_id: number | null;
  pipeline_key: string;
  name: string;
  description: string | null;
  use_cases: string[];
  routing_keywords: string[];
  stages: any[];
  final_artifact_types: string[];
  is_system: boolean;
  enabled: boolean;
  version: number;
  created_at?: string;
  updated_at?: string;
}

export const listAdminPipelines = async (): Promise<AdminPipeline[]> => {
  const { data } = await api.get('/api/smart-start/admin/pipelines');
  return data.pipelines || [];
};

export const createAdminPipeline = async (pipeline: Partial<AdminPipeline>): Promise<AdminPipeline> => {
  const { data } = await api.post('/api/smart-start/admin/pipelines', pipeline);
  return data.pipeline;
};

export const updateAdminPipeline = async (
  id: number,
  updates: Partial<AdminPipeline>
): Promise<AdminPipeline> => {
  const { data } = await api.put(`/api/smart-start/admin/pipelines/${id}`, updates);
  return data.pipeline;
};

export const deleteAdminPipeline = async (id: number): Promise<void> => {
  await api.delete(`/api/smart-start/admin/pipelines/${id}`);
};

export const getVerificationState = async (
  sessionId: number
): Promise<any> => {
  const { data } = await api.get(`/api/smart-start/sessions/${sessionId}/verification-state`);
  return data.verification_state || null;
};

export const recordRemediation = async (
  sessionId: number,
  stageIndex: number,
  decision: {
    finding_id: string;
    action: 'accept' | 'modify' | 'reject' | 'acknowledge';
    notes?: string;
    modified_content?: string;
  }
): Promise<HydratedSmartStartSession> => {
  const { data } = await api.post(
    `/api/smart-start/sessions/${sessionId}/stages/${stageIndex}/remediation`,
    decision,
    { timeout: 0 }
  );
  return { session: data.session, stageRuns: data.stageRuns };
};

export const reVerifyStage = async (
  sessionId: number,
  stageIndex: number
): Promise<HydratedSmartStartSession> => {
  const { data } = await api.post(
    `/api/smart-start/sessions/${sessionId}/stages/${stageIndex}/re-verify`,
    {},
    { timeout: 0 }
  );
  return { session: data.session, stageRuns: data.stageRuns };
};

export class VerificationOverrideError extends Error {
  code: 'OVERRIDE_REASON_REQUIRED' | 'OVERRIDE_REASON_TOO_LONG' | 'UNKNOWN';
  constructor(message: string, code: VerificationOverrideError['code']) {
    super(message);
    this.name = 'VerificationOverrideError';
    this.code = code;
  }
}

export const overrideVerificationGate = async (
  sessionId: number,
  stageIndex: number,
  reason: string
): Promise<HydratedSmartStartSession> => {
  try {
    const { data } = await api.post(
      `/api/smart-start/sessions/${sessionId}/stages/${stageIndex}/override-verification`,
      { reason },
      { timeout: 0 }
    );
    return { session: data.session, stageRuns: data.stageRuns };
  } catch (err: any) {
    const code = err?.response?.data?.code;
    const msg = err?.response?.data?.error || err?.message || 'Override failed';
    if (code === 'OVERRIDE_REASON_REQUIRED' || code === 'OVERRIDE_REASON_TOO_LONG') {
      throw new VerificationOverrideError(msg, code);
    }
    throw err;
  }
};

export const editStageOutput = async (
  sessionId: number,
  stageIndex: number,
  edits: Record<string, any>,
  notes?: string
): Promise<HydratedSmartStartSession> => {
  const { data } = await api.post(
    `/api/smart-start/sessions/${sessionId}/stages/${stageIndex}/edit-output`,
    { edits, notes: notes || null }
  );
  return { session: data.session, stageRuns: data.stageRuns };
};

export interface FixtureCandidate {
  candidate_id: string;
  status: string;
  captured_at: string;
  module: string;
  session_id: number | null;
  stage_key: string | null;
  captured_by_user_id: number | null;
  fixture: {
    description: string;
    fixture: Record<string, any>;
    forbidden_claims: Array<{ phrase: string; match_type?: string; reason?: string }>;
    required_fields: string[];
    tags: string[];
  };
  provenance: {
    verifier_sources: string[];
    severity: string;
    verification_run_at: string | null;
  };
}

export const listFixtureCandidates = async (): Promise<FixtureCandidate[]> => {
  const { data } = await api.get('/api/verification-admin/feedback/candidates');
  return data.candidates || [];
};

export const promoteFixtureCandidate = async (
  candidateId: string
): Promise<{ destination: string }> => {
  const { data } = await api.post(
    `/api/verification-admin/feedback/candidates/${encodeURIComponent(candidateId)}/promote`
  );
  return { destination: data.destination };
};

export const rejectFixtureCandidate = async (
  candidateId: string,
  reason?: string
): Promise<void> => {
  await api.post(
    `/api/verification-admin/feedback/candidates/${encodeURIComponent(candidateId)}/reject`,
    { reason: reason || null }
  );
};

export interface FailurePattern {
  module: string;
  category: string;
  occurrences: number;
  sample_claims: string[];
}

export interface FailurePatternModuleStats {
  module: string;
  runs: number;
  low_confidence_runs: number;
  low_confidence_rate_pct: number;
  avg_confidence: number;
}

export interface FailurePatternReport {
  window_days: number;
  total_traces: number;
  patterns: FailurePattern[];
  module_stats: FailurePatternModuleStats[];
  recommendations: Array<{ module: string; category: string; suggestion: string }>;
  generated_at: string;
}

export const getFailurePatterns = async (
  opts: { windowDays?: number; module?: string; minOccurrences?: number } = {}
): Promise<FailurePatternReport> => {
  const params: Record<string, string> = {};
  if (opts.windowDays) params.window_days = String(opts.windowDays);
  if (opts.module) params.module = opts.module;
  if (opts.minOccurrences) params.min_occurrences = String(opts.minOccurrences);
  const { data } = await api.get('/api/verification-admin/feedback/patterns', { params });
  return data;
};

export interface TelemetryStatus {
  enabled: boolean;
  endpoint: string | null;
  service_name: string;
  sdk_active: boolean;
  sdk_error: string | null;
}

export const getTelemetryStatus = async (): Promise<TelemetryStatus> => {
  const { data } = await api.get('/api/verification-admin/telemetry/status');
  return data.telemetry;
};

export const cloneAdminPipeline = async (
  id: number,
  options?: { suffix?: string; name?: string }
): Promise<AdminPipeline> => {
  const { data } = await api.post(`/api/smart-start/admin/pipelines/${id}/clone`, options || {});
  return data.pipeline;
};

export interface PMProjectSummary {
  id: number;
  name: string;
  description: string | null;
  status: string;
  workstreamCount?: number;
  taskCount?: number;
}

// ---------------------------------------------------------------------------
// Smart Router v2 — orchestrator client
// ---------------------------------------------------------------------------

export const getOrchestratorProposal = async (sessionId: number): Promise<OrchestratorProposal> => {
  const { data } = await api.get(`/api/smart-start/v2/sessions/${sessionId}/proposal`);
  return data.proposal;
};

export const resolveSessionContext = async (
  sessionId: number,
  choice: 'keep_project' | 'use_session',
): Promise<{ applied: boolean; analysis_instructions: string }> => {
  const { data } = await api.post(`/api/smart-start/v2/sessions/${sessionId}/resolve-context`, { choice });
  return { applied: !!data.applied, analysis_instructions: data.analysis_instructions || '' };
};

function hydratedFromResponse(data: any): HydratedOrchestratorSession {
  return {
    session: data.session,
    stageRuns: data.stageRuns,
    navigation_plan: data.navigation_plan,
    intake_slots: data.intake_slots,
    current_step_index: data.current_step_index,
    orchestrator_version: data.orchestrator_version,
    revision: Number(data.revision ?? 0),
    resume_state: data.resume_state || {},
    context_review: data.context_review || { has_conflict: false },
  };
}

export class SessionRevisionConflictError extends Error {
  currentRevision: number;
  expectedRevision: number;
  constructor(currentRevision: number, expectedRevision: number) {
    super('session_revision_conflict');
    this.name = 'SessionRevisionConflictError';
    this.currentRevision = currentRevision;
    this.expectedRevision = expectedRevision;
  }
}

function rethrowConflict(err: any): never {
  if (err?.response?.status === 409 && err.response.data?.error === 'session_revision_conflict') {
    throw new SessionRevisionConflictError(
      Number(err.response.data.current_revision ?? -1),
      Number(err.response.data.expected_revision ?? -1),
    );
  }
  throw err;
}

export const saveIntakeSlots = async (
  sessionId: number,
  slots: Record<string, string>,
  expectedRevision?: number,
): Promise<{ intake_slots: Record<string, string>; revision: number }> => {
  try {
    const { data } = await api.put(`/api/smart-start/v2/sessions/${sessionId}/intake-slots`, {
      slots,
      expected_revision: expectedRevision,
    });
    return { intake_slots: data.intake_slots, revision: Number(data.revision ?? 0) };
  } catch (err) { rethrowConflict(err); throw err; }
};

export const selectOrchestratorPipeline = async (
  sessionId: number,
  pipelineKey: string,
  expectedRevision?: number,
): Promise<HydratedOrchestratorSession> => {
  try {
    const { data } = await api.post(`/api/smart-start/v2/sessions/${sessionId}/select-pipeline`, {
      pipeline_key: pipelineKey,
      expected_revision: expectedRevision,
    });
    return hydratedFromResponse(data);
  } catch (err) { rethrowConflict(err); throw err; }
};

export const getOrchestratorSession = async (sessionId: number): Promise<HydratedOrchestratorSession> => {
  const { data } = await api.get(`/api/smart-start/v2/sessions/${sessionId}`);
  return hydratedFromResponse(data);
};

export const syncOrchestratorFromEngagement = async (
  sessionId: number,
  engagementId: number,
  stage: string,
): Promise<HydratedOrchestratorSession> => {
  const { data } = await api.post(
    `/api/smart-start/v2/sessions/${sessionId}/sync-from-engagement`,
    { engagement_id: engagementId, stage },
  );
  return hydratedFromResponse(data);
};

export const markOrchestratorStepDone = async (
  sessionId: number,
  stepKey: string,
  options: {
    artifact?: Record<string, any>;
    summary?: string | null;
    autoAdvance?: boolean;
  } = {},
): Promise<HydratedOrchestratorSession> => {
  const { data } = await api.post(
    `/api/smart-start/v2/sessions/${sessionId}/mark-step-done`,
    {
      step_key: stepKey,
      artifact: options.artifact ?? null,
      summary: options.summary ?? null,
      auto_advance: options.autoAdvance !== false,
    },
  );
  return hydratedFromResponse(data);
};

export const heartbeatOrchestratorStep = async (
  sessionId: number,
  stepIndex: number,
  resumeExtra: Record<string, any>,
): Promise<{ last_seen_at: string }> => {
  const { data } = await api.post(
    `/api/smart-start/v2/sessions/${sessionId}/steps/${stepIndex}/heartbeat`,
    { resume_extra: resumeExtra },
  );
  return { last_seen_at: data.last_seen_at };
};

export const issueResumeLink = async (
  sessionId: number,
  stepIndex: number,
): Promise<DeepLinkIssueResponse> => {
  const { data } = await api.post(
    `/api/smart-start/v2/sessions/${sessionId}/steps/${stepIndex}/resume-link`,
    {},
  );
  return {
    inline: !!data.inline,
    step: data.step as PlanStep,
    url: data.url ?? null,
    token: data.token ?? null,
    expires_at: data.expires_at ?? null,
  };
};

export const issueStepDeepLink = async (
  sessionId: number,
  stepIndex: number,
  expectedRevision?: number,
): Promise<DeepLinkIssueResponse> => {
  try {
    const { data } = await api.post(
      `/api/smart-start/v2/sessions/${sessionId}/steps/${stepIndex}/deep-link`,
      { expected_revision: expectedRevision },
    );
    return {
      inline: !!data.inline,
      step: data.step as PlanStep,
      url: data.url ?? null,
      token: data.token ?? null,
      expires_at: data.expires_at ?? null,
    };
  } catch (err) { rethrowConflict(err); throw err; }
};

export interface PipelineLaunchResponse {
  session_id: number;
  pipeline_key: string;
  requires_documents: boolean;
  target_screen: string | null;
  target_step_index: number | null;
  deep_link_url: string | null;
  inline?: boolean;
  upload_followup?: boolean;
  message?: string;
  no_actionable_step?: boolean;
}

export const launchPipeline = async (
  pipelineKey: string,
  options?: {
    projectId?: number | null;
    goalText?: string | null;
    attachedDocuments?: AttachedDocument[];
  },
): Promise<PipelineLaunchResponse> => {
  const { data } = await api.post(
    '/api/smart-start/v2/launch-pipeline',
    {
      pipeline_key: pipelineKey,
      project_id: options?.projectId ?? null,
      goal_text: options?.goalText ?? null,
      attached_documents: options?.attachedDocuments || [],
    },
    { timeout: 0 },
  );
  return data;
};

export interface ResumeAfterUploadResponse {
  session_id: number;
  target_step_index: number;
  target_screen: string;
  deep_link_url: string;
  inline?: boolean;
}

export const resumeAfterUpload = async (
  sessionId: number,
): Promise<ResumeAfterUploadResponse> => {
  const { data } = await api.post(
    `/api/smart-start/v2/sessions/${sessionId}/resume-after-upload`,
  );
  return data;
};

export interface ConversationalTurn {
  role: 'user' | 'assistant';
  content: string;
  slot_key?: string;
  why_asking?: string;
  ts?: string;
}

export interface ConversationalLaunchResponse {
  session_id: number;
  ready: boolean;
  pipeline_key?: string;
  pipeline_name?: string;
  no_match?: boolean;
  message?: string;
  next_question?: {
    slot_key: string;
    prompt: string;
    why_asking?: string;
  } | null;
  filled_slots?: Record<string, string>;
  inferred_summary?: string;
  turns_asked?: number;
  max_turns?: number;
  target_screen?: string | null;
  target_step_index?: number | null;
  target_step_label?: string;
  deep_link_url?: string | null;
  inline?: boolean;
  upload_followup?: boolean;
  no_actionable_step?: boolean;
}

export const conversationalLaunch = async (payload: {
  sessionId?: number | null;
  goalText?: string | null;
  projectId?: number | null;
  conversationHistory?: ConversationalTurn[];
  userAnswer?: string | null;
  skipRemaining?: boolean;
}): Promise<ConversationalLaunchResponse> => {
  const { data } = await api.post(
    '/api/smart-start/v2/conversational-launch',
    {
      session_id: payload.sessionId ?? null,
      goal_text: payload.goalText ?? null,
      project_id: payload.projectId ?? null,
      conversation_history: payload.conversationHistory || [],
      user_answer: payload.userAnswer ?? null,
      skip_remaining: payload.skipRemaining === true,
    },
    { timeout: 0 },
  );
  return data;
};

export interface OrchestratorCompletePayload {
  token: string;
  artifact?: {
    refs?: Array<Record<string, any>>;
    output_payload?: Record<string, any>;
    verifiable_text?: string;
    [key: string]: any;
  };
  summary?: string | null;
  chat_transcript?: Array<{ role: 'user' | 'assistant' | 'system'; content: string; ts?: string; meta?: Record<string, any> }>;
}

export interface ExportBlockedDetails {
  cumulative_confidence: number;
  threshold: number;
  pending_findings: number;
  low_stages: string[];
  low_stage_details?: Array<{
    stage_key: string;
    stage_index: number | null;
    stage_label: string;
    composite_score: number | null;
    band: string | null;
  }>;
}

export class ExportBlockedError extends Error {
  details: ExportBlockedDetails;
  constructor(message: string, details: ExportBlockedDetails) {
    super(message);
    this.name = 'ExportBlockedError';
    this.details = details;
  }
}

export const completeOrchestratorStep = async (
  sessionId: number,
  stepIndex: number,
  payload: OrchestratorCompletePayload,
): Promise<HydratedOrchestratorSession> => {
  try {
    const { data } = await api.post(
      `/api/smart-start/v2/sessions/${sessionId}/steps/${stepIndex}/complete`,
      payload,
      { timeout: 0 },
    );
    return hydratedFromResponse(data);
  } catch (err: any) {
    const code = err?.response?.data?.code;
    if (code === 'EXPORT_BLOCKED_BY_VERIFICATION') {
      const details = (err.response.data.details || {}) as ExportBlockedDetails;
      throw new ExportBlockedError(err.response.data.error || 'Export blocked', details);
    }
    throw err;
  }
};

export const rejectOrchestratorStep = async (
  sessionId: number,
  stepIndex: number,
  reason?: string,
  expectedRevision?: number,
): Promise<HydratedOrchestratorSession> => {
  try {
    const { data } = await api.post(
      `/api/smart-start/v2/sessions/${sessionId}/steps/${stepIndex}/reject`,
      { reason: reason || null, expected_revision: expectedRevision },
    );
    return hydratedFromResponse(data);
  } catch (err) { rethrowConflict(err); throw err; }
};

export const downloadArtifact = async (url: string, fileName: string): Promise<void> => {
  const response = await api.get(url, { responseType: 'blob', timeout: 120000 });
  const blob = response.data instanceof Blob ? response.data : new Blob([response.data]);
  const objectUrl = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = fileName;
  a.rel = 'noopener';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1000);
};

export const listPMProjects = async (): Promise<PMProjectSummary[]> => {
  try {
    const { data } = await api.get('/api/pm-projects?status=active');
    return (data.projects || []).map((p: any) => ({
      id: p.id,
      name: p.name,
      description: p.description,
      status: p.status,
      workstreamCount: p.workstreamCount,
      taskCount: p.taskCount,
    }));
  } catch (err) {
    console.error('Could not load PM projects for Smart Start prefill:', err);
    return [];
  }
};
